import { Module, VuexModule, Action, Mutation, MutationAction } from "vuex-module-decorators";
import { remove, groupBy, orderBy, cloneDeep } from "lodash";
import { store, fmtMsg } from "commonui";
import {
	EmptyRepConversation,
	IContentConversationRepModel,
	ILessonPlanListModel,
	ILessonPlanVersionModel
} from "@/models/lesson-plan";
import { IUnitListManager, UnitListManagerService } from "@/services/text-guardian/unit-list";
import { VCSLevelModel } from "@/models/title-list-model";
import {
	ComponentTaughtType,
	IComponentModel,
	ILessonPlanItemModel,
	ILessonPlanRowModel,
	LessonPlanItemType,
	IMasterPlan4DBModel,
	ILessonPlan4DBModel,
	ILessonPlanItem4DBModel,
	LessonPlanItemType4DB,
	MasterLessonPlanAction,
	PlaylistItemType,
	ICustomActivityModel
} from "@/models/master-plan";
import {
	ILessonPlanListService,
	ILessonPlanVersionService,
	LessonPlanListService,
	LessonPlanVersionService
} from "@/services/lesson-plan";
import { ResourceService } from "@/services/resource";
import { EmptyTransition, TransitionModel, TransitionService } from "@/services/transition";
import { MethodologyTypeEnum, ContentGroupEnum, Activity } from "@/util/enum";
import { IVersionGroupService, VersionGroupModel, VersionGroupService } from "@/services";
import { EstimatedTimeRowKey, TransitionRowKey, QuestionOrConversationRowKey } from "@/util/constants";
import { deepClone, getConversationTitle } from "@/util/func";
import { ContentExposureService } from "@/services/content-exposure";
import { TitleListService } from "@/services/title-list-service";
import { LessonPlanLocale } from "@/locales/localeid";
import { TeachingActivityService } from "@/services/teaching-activity";
import { ContainerType, SignatureResoureModel } from "@/services/admin";
import { ILessonPlanActivitiesService, LessonPlanActivitiesService } from "@/services/lesson-plan-activities";

const minExposureCountInLessonPlan: number = 0;
const defaultEstimateTime: number = 0;

function setEstimatedTime(
	lessonPlans: ILessonPlanRowModel[],
	transitions: TransitionModel[],
	lessons: ILessonPlanListModel[],
	customActivites: ICustomActivityModel[]
): void {
	const estimatedTimeRow: ILessonPlanRowModel | undefined = lessonPlans.find(
		(lp) => lp.rowKey === EstimatedTimeRowKey
	);
	if (estimatedTimeRow === undefined) {
		return;
	}

	estimatedTimeRow.estimatedTime = lessons.map((l, lessonIndex) => {
		return lessonPlans
			.filter((lp) => lp.rowType === LessonPlanItemType.Component || lp.rowType === LessonPlanItemType.Activity)
			.reduce((pre, cur, index) => {
				if (cur.rowType === LessonPlanItemType.Component) {
					const lessonPlanNumbers: number[] = cur.lessonPlanItems
						.filter((lpi) => lpi.taughtType === ComponentTaughtType.Normal)
						.map((lpi) => lpi.lessonNumber);
					const exposureIndex: number = lessonPlanNumbers.findIndex((lpn) => lpn === lessonIndex + 1);
					const exposure: number =
						cur.component &&
						cur.component.id &&
						cur.component.relatedExposureDurations.length > exposureIndex &&
						exposureIndex >= 0
							? (cur.component as IComponentModel).relatedExposureDurations[exposureIndex]
							: 0;
					return pre + exposure;
				} else if (cur.rowType === LessonPlanItemType.Activity) {
					const customActivityTime = 
						cur.lessonPlanItems
							.filter((lpi) => lpi.lessonNumber === lessonIndex + 1)
							.reduce((pre, cur, index) => {
								const activity = customActivites.find((t) => t.id === cur.activityId) || ({} as ICustomActivityModel);
								return pre + (activity.id ? activity.maxDuration-activity.minDuration : 0);
							}, 0);
					return (
						pre +
						customActivityTime +
						cur.lessonPlanItems
							.filter((lpi) => lpi.lessonNumber === lessonIndex + 1)
							.reduce((pre, cur, index) => {
								const transition: TransitionModel =
									transitions.find((t) => t.id === cur.componentId) || ({} as TransitionModel);
								return pre + (transition.id ? transition.duration : 0);
							}, 0)
					);
				} else {
					return pre;
				}
			}, 0);
	});
}

function getTransitionRow(lessonPlans: ILessonPlanRowModel[], isREPMethodology: boolean = false): ILessonPlanRowModel {
	return lessonPlans.find((lp) =>
		lp.activityId === Activity.Question || lp.activityId === Activity.Conversation ? lp.rowKey === QuestionOrConversationRowKey : lp.rowKey === TransitionRowKey
		// isREPMethodology ? lp.rowKey === QuestionOrConversationRowKey : lp.rowKey === TransitionRowKey
	) as ILessonPlanRowModel;
}

function getLessonPlans(lessonPlans: ILessonPlanRowModel[], lessonNumber: number): ILessonPlanItemModel[] {
	const allLessons: ILessonPlanItemModel[] = lessonPlans.reduce((pre, cur) => {
		return cur.lessonPlanItems ? pre.concat(cur.lessonPlanItems) : pre.concat([]);
	}, [] as ILessonPlanItemModel[]);
	return allLessons.filter((al) => al.lessonNumber === lessonNumber);
}

function getLessonTransitions(
	lessonPlans: ILessonPlanRowModel[],
	lessonNumber: number,
	taughtType: ComponentTaughtType,
	isCloneMode: boolean
): ILessonPlanItemModel[] {
	const currentLessons: ILessonPlanItemModel[] = getLessonPlans(lessonPlans, lessonNumber)
		.filter((lpi) => lpi.taughtType === taughtType)
		.sort((lpiA, lpiB) => (lpiA.lessonSequence as number) - (lpiB.lessonSequence as number));
	return isCloneMode
		? currentLessons.map((lpi) => {
				return { ...lpi };
		  })
		: currentLessons;
}

function resetLessonSequenceOrders(
	lessons: ILessonPlanItemModel[],
	taughtType: ComponentTaughtType = ComponentTaughtType.Normal
) {
	lessons
		.filter((cl) => cl.taughtType === taughtType)
		.sort((a, b) => (a.lessonSequence as number) - (b.lessonSequence as number))
		.forEach((cl, index) => (cl.lessonSequence = index + 1));
}

function updateLessonSequenceOrders4White2Green(lessons: ILessonPlanItemModel[]) {
	lessons
		.filter((cl) => cl.taughtType === ComponentTaughtType.Optional)
		.forEach((cl, index) => (cl.lessonSequence = cl.lessonSequence ? (cl.lessonSequence as number) + 1 : 1));
}

function updateLessonSequenceOrders4Green2Yellow(lessons: ILessonPlanItemModel[], greenSequence: number) {
	lessons
		.filter((cl) => (cl.lessonSequence as number) > greenSequence)
		.forEach((cl, index) => (cl.lessonSequence = cl.lessonSequence ? (cl.lessonSequence as number) - 1 : 1));
}

function updateLessonSequenceOrders4Yellow2White(lessons: ILessonPlanItemModel[], yellowSequence: number) {
	lessons
		.filter(
			(cl) => cl.taughtType === ComponentTaughtType.Optional && (cl.lessonSequence as number) > yellowSequence
		)
		.forEach((cl, index) => (cl.lessonSequence = cl.lessonSequence ? (cl.lessonSequence as number) - 1 : 1));
}

function updateLessonSequenceOrders4Green2White(lessons: ILessonPlanItemModel[], greenSequence: number) {
	lessons
		.filter((cl) => (cl.lessonSequence as number) > greenSequence)
		.forEach((cl, index) => (cl.lessonSequence = cl.lessonSequence ? (cl.lessonSequence as number) - 1 : 1));
}

function setLessonPlanItem4Transition(
	lessonPlans: ILessonPlanRowModel[],
	lessonNumber: number,
	transition: TransitionModel,
	transitions?: TransitionModel[],
	lessonTransition?: ILessonPlanItemModel
) {
	const currentLessons: ILessonPlanItemModel[] = getLessonPlans(lessonPlans, lessonNumber);
	const normalLessons: ILessonPlanItemModel[] = currentLessons.filter(
		(cl) => cl.taughtType === ComponentTaughtType.Normal
	);
	const newLessonSequence: number = normalLessons.length + 1;
	const transitionRow: ILessonPlanRowModel = getTransitionRow(lessonPlans);
	const estimatedTimeRow: ILessonPlanRowModel | undefined = lessonPlans.find(
		(lp) => lp.rowKey === EstimatedTimeRowKey
	);
	const estimatedTime: number[] = estimatedTimeRow === undefined ? [] : [...estimatedTimeRow.estimatedTime];
	if (lessonTransition) {
		const lessonTransitionIndex: number = transitionRow.lessonPlanItems.findIndex(
			(lpi) => lpi.lessonNumber === lessonNumber && lpi.lessonSequence === lessonTransition.lessonSequence
		);
		if (lessonTransitionIndex >= 0) {
			transitionRow.lessonPlanItems.splice(lessonTransitionIndex, 1);
		}
		const lessonIndex: number = currentLessons.findIndex(
			(cl) => cl.lessonNumber === lessonNumber && cl.lessonSequence === lessonTransition.lessonSequence
		);
		if (lessonIndex >= 0) {
			currentLessons.splice(lessonIndex, 1);
		}
		// resetLessonSequenceOrders(currentLessons);
		updateLessonSequenceOrders4Green2White(currentLessons, lessonTransition.lessonSequence as number);
		// update estimate time for transition
		if (estimatedTimeRow) {
			const removedTransition: TransitionModel = transitions
				? transitions.find((t) => t.id === lessonTransition.componentId) || ({} as TransitionModel)
				: ({} as TransitionModel);
			estimatedTime[lessonNumber - 1] =
				estimatedTime[lessonNumber - 1] - (removedTransition.duration ? removedTransition.duration : 0);
			estimatedTimeRow.estimatedTime = estimatedTime;
		}
	} else {
		transitionRow.lessonPlanItems.push({
			componentId: transition.id,
			componentName: transition.text,
			itemType: LessonPlanItemType.Activity,
			lessonNumber,
			taughtType: ComponentTaughtType.Normal,
			lessonSequence: newLessonSequence,
			activityId: Activity.Transition
		});
		updateLessonSequenceOrders4White2Green(currentLessons);
		// update estimate time for transition
		if (estimatedTimeRow) {
			estimatedTime[lessonNumber - 1] = estimatedTime[lessonNumber - 1] + transition.duration;
			estimatedTimeRow.estimatedTime = estimatedTime;
		}
	}
}

function setLessonPlanItem(
	lessonPlans: ILessonPlanRowModel[],
	lessons: ILessonPlanListModel[],
	transitions: TransitionModel[],
	data: any,
	isREPMethodology: boolean = false,
	customActivites: ICustomActivityModel[],
): void {
	const { lessonPlanRowIndex, lessonPlanRow, lessonNumber, lessonPlanItem: currentlessonPlanItem } = data;
	const currentNormalLessonPlanItems: ILessonPlanItemModel[] = lessonPlanRow.lessonPlanItems.filter(
		(lpis) => lpis.taughtType === ComponentTaughtType.Normal
	);
	const component: IComponentModel = lessonPlanRow.component;
	const exposureCount: number =
		component.relatedExposureDurations && component.relatedExposureDurations.length > 0
			? component.relatedExposureDurations.length
			: minExposureCountInLessonPlan;
	const currentLessons: ILessonPlanItemModel[] = getLessonPlans(lessonPlans, lessonNumber);
	const normalLessons: ILessonPlanItemModel[] = currentLessons.filter(
		(cl) => cl.taughtType === ComponentTaughtType.Normal
	);
	const getNormalOptionalLessons = () => {
		return currentLessons.filter((cl) => cl.taughtType !== ComponentTaughtType.None);
	};
	const newNormalLessonSequence: number = normalLessons.length + 1;
	if (
		lessonPlanRow.rowKey === EstimatedTimeRowKey ||
		lessonPlanRow.rowKey === TransitionRowKey ||
		lessonPlanRow.rowKey === QuestionOrConversationRowKey
	) {
		return;
	} else if (currentNormalLessonPlanItems.length === exposureCount && !currentlessonPlanItem) {
		!isREPMethodology &&
			lessonPlans[lessonPlanRowIndex].lessonPlanItems.push({
				componentId: component.id,
				componentName: component.title,
				itemType: LessonPlanItemType.Component,
				lessonNumber,
				taughtType: ComponentTaughtType.Optional,
				lessonSequence: getNormalOptionalLessons().length + 1,
				mediaSlotId: component.mediaSlotId
			});
		return;
	}
	if (currentlessonPlanItem) {
		switch (currentlessonPlanItem.taughtType) {
			case ComponentTaughtType.Normal:
				currentlessonPlanItem.taughtType = isREPMethodology
					? ComponentTaughtType.None
					: ComponentTaughtType.Optional;
				isREPMethodology
					? updateLessonSequenceOrders4Green2White(currentLessons, currentlessonPlanItem.lessonSequence)
					: updateLessonSequenceOrders4Green2Yellow(currentLessons, currentlessonPlanItem.lessonSequence);
				currentlessonPlanItem.lessonSequence = getNormalOptionalLessons().length;
				setEstimatedTime(lessonPlans, transitions, lessons, customActivites);
				break;
			case ComponentTaughtType.Optional:
				currentlessonPlanItem.taughtType = ComponentTaughtType.None;
				updateLessonSequenceOrders4Yellow2White(currentLessons, currentlessonPlanItem.lessonSequence);
				break;
			default:
				if (
					currentNormalLessonPlanItems.length === exposureCount ||
					exposureCount === minExposureCountInLessonPlan
				) {
					if (!isREPMethodology) {
						currentlessonPlanItem.taughtType = ComponentTaughtType.Optional;
						currentlessonPlanItem.lessonSequence = getNormalOptionalLessons().length;
					}
				} else {
					currentlessonPlanItem.taughtType = ComponentTaughtType.Normal;
					currentlessonPlanItem.lessonSequence = newNormalLessonSequence;
					updateLessonSequenceOrders4White2Green(currentLessons);
					setEstimatedTime(lessonPlans, transitions, lessons, customActivites);
				}
				break;
		}
	} else {
		lessonPlans[lessonPlanRowIndex].lessonPlanItems.push({
			componentId: component.id,
			componentName: component.name,
			itemType: LessonPlanItemType.Component,
			lessonNumber,
			taughtType: ComponentTaughtType.Normal,
			lessonSequence: newNormalLessonSequence,
			mediaSlotId: component.mediaSlotId
		});
		updateLessonSequenceOrders4White2Green(currentLessons);
		setEstimatedTime(lessonPlans, transitions, lessons, customActivites);
	}
	const changedNormalLessonPlanItems: ILessonPlanItemModel[] = lessonPlans[lessonPlanRowIndex].lessonPlanItems.filter(
		(lpis) => lpis.taughtType === ComponentTaughtType.Normal
	);
	lessonPlanRow.exposure =
		exposureCount > minExposureCountInLessonPlan ? `${changedNormalLessonPlanItems.length}/${exposureCount}` : "";
}

function filterUnits(units: VCSLevelModel[], versionGroup: VersionGroupModel): VCSLevelModel[] {
	return units.filter((au) => au.curriculumType === versionGroup.curriculumType).sort((ua, ub) => ua.unit - ub.unit);
}

function getLessons(masterPlan: IMasterPlan4DBModel): ILessonPlanListModel[] {
	return masterPlan.lessonPlan
		.sort((lpA, lpB) => (lpA.sequence as number) - (lpB.sequence as number))
		.map((mp, index) => {
			return {
				id: mp.id,
				sequence: index + 1,
				name: `Lesson ${mp.sequence}`,
				conversationSpeed: mp.conversationSpeed
			};
		});
}

function getLessonPlanItemTaughtType(type?: LessonPlanItemType4DB): ComponentTaughtType {
	switch (type) {
		case LessonPlanItemType4DB.ExposureContent:
			return ComponentTaughtType.Normal;
		case LessonPlanItemType4DB.OptionalContent:
			return ComponentTaughtType.Optional;
		default:
			return ComponentTaughtType.None;
	}
}

function checkActivity(lpi, transition, repConversations, customActivities) {
	switch(lpi.activityId) {
		case 2: return transition ? transition.text : ""
		case 3: return fmtMsg(LessonPlanLocale.LessonPlanPlayListBuilderCellQuestionTitle)
		case 4: return getConversationTitle(repConversations.find((r) => r.contentId === lpi.componentId) as any, false)
		default: return customActivities.find(ca => ca.id === lpi.activityId).name
	}
}

function getLessonPlanItemModel4Transition(
	lpi: ILessonPlanItem4DBModel,
	lessonNumber: number,
	transitions: TransitionModel[],
	lessonPlanItem: ILessonPlanItem4DBModel[],
	isREPMethodology: boolean,
	repConversations: IContentConversationRepModel[],
	customActivities
): ILessonPlanItemModel {
	const transition: TransitionModel | undefined = transitions.find((t) => t.id === lpi.componentId);
	transitions.find((t) => {if(t.id === lpi.componentId) {lpi.activityId = Activity.Transition}})
	return {
		activityId: lpi.activityId,
		itemType: LessonPlanItemType.Activity,
		componentId: lpi.componentId,
		componentName: checkActivity(lpi, transition, repConversations, customActivities),
		lessonNumber: lessonNumber,
		taughtType: ComponentTaughtType.Normal,
		lessonSequence: lpi.sequence
	};
}

function getLessonPlanItemModel(
	component: IComponentModel,
	lessonPlan: ILessonPlan4DBModel,
	lessonNumber: number
): ILessonPlanItemModel {
	const lpi: ILessonPlanItem4DBModel | undefined = lessonPlan.lessonPlanItem?.find(
		(lpi) => lpi.componentId === component.id && lpi.mediaSlotId === component.mediaSlotId
	);
	return lpi
		? {
				itemType: LessonPlanItemType.Component,
				componentId: component.id,
				componentName: component.title,
				lessonNumber: lessonNumber,
				taughtType: getLessonPlanItemTaughtType(lpi.type),
				lessonSequence: lpi.sequence,
				mediaSlotId: lpi.mediaSlotId
		  }
		: {
				itemType: LessonPlanItemType.Component,
				componentId: component.id,
				componentName: component.title,
				lessonNumber: lessonNumber,
				taughtType: ComponentTaughtType.None,
				lessonSequence: undefined,
				mediaSlotId: component.mediaSlotId
		  };
}

function getComponentExposure(component: IComponentModel, masterPlan: IMasterPlan4DBModel): string {
	const componentExposure: number = masterPlan.lessonPlan.filter(
		(lp) =>
			lp.lessonPlanItem.filter(
				(lpi) =>
					lpi.componentId === component.id &&
					lpi.type === LessonPlanItemType4DB.ExposureContent &&
					lpi.mediaSlotId === component.mediaSlotId
			).length > 0
	).length;
	const exposureCount: number = component.relatedExposureDurations.length;
	return exposureCount === 0 ? "" : `${componentExposure}/${exposureCount}`;
}

function getComponentLessonPlans(componentId: string, masterPlan: IMasterPlan4DBModel): string[] {
	return masterPlan.lessonPlan
		.filter(
			(lp) =>
				lp.lessonPlanItem.filter(
					(lpi) => lpi.componentId === componentId && lpi.type === LessonPlanItemType4DB.ExposureContent
				).length > 0
		)
		.map((lp) => {
			const lpi: ILessonPlanItem4DBModel | undefined = lp.lessonPlanItem.find(
				(lpi) => lpi.componentId === componentId
			);
			return lpi?.lessonPlanId || "";
		});
}

function getCustomActivityDuration(customActivity: ICustomActivityModel): number {
	if(!customActivity.minDuration && !customActivity.maxDuration) {
		return 0
	}
	return customActivity.maxDuration - customActivity.minDuration;
}

function getEstimatedTime(
	components: IComponentModel[],
	transitions: TransitionModel[],
	masterPlan: IMasterPlan4DBModel,
	customActivites: ICustomActivityModel[]
): number[] {
	return masterPlan.lessonPlan.map((lp, lpIndex) => {
		return lp.lessonPlanItem
			.filter(
				(lpi) =>
					lpi.type === LessonPlanItemType4DB.ExposureContent ||
					lpi.type === LessonPlanItemType4DB.Activity
			)
			.reduce((pre, cur, lpiIndex) => {
				/*
					Applied check "c.mediaSlotId === cur.mediaSlotId" while finding component becuase
					while finding component in REP LPVs if there were two exposures of same component but with different media slots (audio or video)
					without this check same component exposure was returned and hence estimated time calculated was wrong.
					As for other than REP LPVs the mediaSlotId is null and the check will always be true in that case.
				*/
				const component: IComponentModel =
					components.find((c) => c.id === cur.componentId && c.mediaSlotId === cur.mediaSlotId) ||
					({} as IComponentModel);
				const componentLessonPlans: string[] = getComponentLessonPlans(component.id || "", masterPlan);
				const exposureDurationIndex: number = componentLessonPlans.findIndex((clp) => clp === cur.lessonPlanId);
				const transition: TransitionModel =
					transitions.find((t) => t.id === cur.componentId) || ({} as TransitionModel);
				const customActivity: ICustomActivityModel = customActivites.find((t) => t.id === cur.activityId) || ({} as ICustomActivityModel);
				const curDuration: number =
					cur.type === LessonPlanItemType4DB.ExposureContent
						? component.id &&
						  component.relatedExposureDurations.length > exposureDurationIndex &&
						  exposureDurationIndex >= 0
							? component.relatedExposureDurations[exposureDurationIndex]
							: 0
						: transition.id
						? transition.duration
						: getCustomActivityDuration(customActivity);
				return pre + curDuration;
			}, 0);
	});
}

function formatTransitions(transitions: TransitionModel[]): TransitionModel[] {
	transitions.unshift(EmptyTransition);
	return transitions;
}

function formatConversations(conversations: IContentConversationRepModel[]): IContentConversationRepModel[] {
	conversations.unshift(EmptyRepConversation);
	return conversations;
}

function formatLessonPlans(
	components: IComponentModel[],
	unit: VCSLevelModel,
	masterPlan: IMasterPlan4DBModel,
	transitions: TransitionModel[],
	isREPMethodology: boolean,
	repConversations: IContentConversationRepModel[],
	customActivities: ICustomActivityModel[],
): ILessonPlanRowModel[] {
	const result: ILessonPlanRowModel[] = [];
	result.push({
		rowType: LessonPlanItemType.EstimatedTime,
		rowKey: EstimatedTimeRowKey,
		lessonPlanItems: [],
		estimatedTime: getEstimatedTime(components, transitions, masterPlan, customActivities)
	});
	result.push({
		activityId: isREPMethodology ? Activity.Question : Activity.Transition,
		rowType: LessonPlanItemType.Activity,
		rowKey: isREPMethodology ? QuestionOrConversationRowKey : TransitionRowKey,
		// rowKey: TransitionRowKey,
		lessonPlanItems: masterPlan.lessonPlan
			.sort((lpA, lpB) => (lpA.sequence as number) - (lpB.sequence as number))
			.reduce((pre, cur, index) => {
				const transitionLPIs: ILessonPlanItem4DBModel[] = cur.lessonPlanItem.filter((lpi) =>
				lpi.type === LessonPlanItemType4DB.Activity
				);
				return pre.concat(
					transitionLPIs.map((tlpi) =>
						getLessonPlanItemModel4Transition(
							tlpi,
							index + 1,
							transitions,
							cur.lessonPlanItem,
							isREPMethodology,
							repConversations,
							customActivities
						)
					)
				);
			}, [] as ILessonPlanItemModel[]),
		estimatedTime: []
	});
	const formatComponents = orderBy(components, [(c) => c.title?.toLowerCase(), "contentGroupId"], ["asc", "asc"]);
	return result.concat(
		formatComponents.map((c) => {
			return {
				rowType: LessonPlanItemType.Component,
				rowKey: `${c.id}${c.mediaSlotId ? c.mediaSlotId : null}`,
				component: {
					id: c.id,
					name: c.title,
					contentGroupId: c.contentGroupId,
					relatedExposureDurations: c.relatedExposureDurations,
					mediaSlotId: c.mediaSlotId
				},
				exposure: getComponentExposure(c, masterPlan),
				lessonPlanItems: masterPlan.lessonPlan
					.filter((lp) =>
						lp.lessonPlanItem.filter((lpi) =>
							{}
						)
					)
					.sort((lpA, lpB) => (lpA.sequence as number) - (lpB.sequence as number))
					.map((lp, index) => {
						return getLessonPlanItemModel(c, lp, index + 1);
					})
			} as ILessonPlanRowModel;
		})
	);
}

function getLessonPlanActivity4DB(isREPMethodology: boolean, activityId) {
	switch (activityId) {
		case 2: return Activity.Transition;
		case 3: return Activity.Question;
		case 4: return Activity.Conversation;
		default: return activityId
	}
}

function getLessonPlanItemType4DB(isREPMethodology: boolean, type?: ComponentTaughtType): LessonPlanItemType4DB {
	switch (type) {
		case ComponentTaughtType.Normal:
			return LessonPlanItemType4DB.ExposureContent;
		case ComponentTaughtType.Optional:
			return LessonPlanItemType4DB.OptionalContent;
		default:
			return LessonPlanItemType4DB.Activity
	}
}

function getLessonPlanItem4DBModel(
	lesson: ILessonPlanListModel,
	lessonPlan: ILessonPlanRowModel,
	isREPMethodology: boolean
): ILessonPlanItem4DBModel {
	const lpi: ILessonPlanItemModel | undefined = lessonPlan.lessonPlanItems?.find(
		(lpi) => lpi.lessonNumber === lesson.sequence
	);
	return lpi
		? {
				componentId: lpi.componentId,
				sequence: lpi.lessonSequence,
				type: getLessonPlanItemType4DB(isREPMethodology, lpi.taughtType),
				activityId: getLessonPlanActivity4DB(isREPMethodology, lpi.activityId),
				mediaSlotId: lpi.mediaSlotId
		  }
		: ({} as ILessonPlanItem4DBModel);
}

function formatLessonPlans4Save(
	lessons: ILessonPlanListModel[],
	lessonPlans: ILessonPlanRowModel[],
	isREPMethodology: boolean
): ILessonPlan4DBModel[] {
	return lessons.map((l) => {
		return {
			id: l.id,
			action: l.id ? MasterLessonPlanAction.Updating : MasterLessonPlanAction.Adding,
			lessonPlanItem: lessonPlans
				.filter((lp) =>
					lp.lessonPlanItems.find(
						(lpi) =>
							lpi.itemType === LessonPlanItemType.Component &&
							lpi.lessonNumber === l.sequence &&
							lpi.taughtType !== ComponentTaughtType.None
					)
				)
				.map((lpi) => getLessonPlanItem4DBModel(l, lpi, isREPMethodology))
				.concat(
					getTransitionRow(lessonPlans, isREPMethodology)
						.lessonPlanItems.filter((lpi) => lpi.lessonNumber === l.sequence)
						.map((lpi) => {
							return {
								componentId: lpi.componentId,
								sequence: lpi.lessonSequence,
								type: LessonPlanItemType4DB.Activity,
								activityId: lpi.activityId
							} as ILessonPlanItem4DBModel;
						})
				),
			conversationSpeed: l.conversationSpeed
		} as ILessonPlan4DBModel;
	});
}
function toTimeString(second) {
	const sec = second % 60;
	return `${Math.floor(second / 60)}:${sec > 9 ? sec : `0${sec}`}`;
}
function formatLessonPlanEditItems(lessonPlanEditItems: any[], pageSignature: SignatureResoureModel) {
	const getImageUrl = (path: string) => {
		if (pageSignature) {
			const splitedUrl = pageSignature.url.split("?");
			const signatureBase = splitedUrl[0] + pageSignature.name + "/";
			const signatureParams = "?" + splitedUrl[1];

			return signatureBase + path + signatureParams;
		}

		return path;
	};

	return lessonPlanEditItems.map((lessonPlanItem) => {
		const contentExposure = lessonPlanItem.contentExposure;
		switch (lessonPlanItem.type) {
			case LessonPlanItemType4DB.ExposureContent:
				lessonPlanItem.componentContent = contentExposure.content;
				lessonPlanItem.duration = {
					start: toTimeString(contentExposure.minPlanningTime),
					end: toTimeString(contentExposure.maxPlanningTime)
				};
				lessonPlanItem.relatedItems = contentExposure.contentExposureRelatedItem.map((item) => ({
					contentId: item.contentId,
					type: item.type,
					title: item.content?.title || item.text,
					vcsLevelId: item.content?.vcsLevelId,
					unitName: item.content?.vcsLevel?.displayName,
					content: { sequence: item.content?.sequence },
					unit: item.content?.vcsLevel?.unit,
					text: item.text,
					sequence: item.sequence,
					parentTypeId: item.content?.parentTypeId,
					typeSequence: item.typeSequence,
					contentType: item.content?.contentType,
					typeName: item.content?.contentType?.name
				}));
				lessonPlanItem.teachingActivities = contentExposure.contentExposureTeachingActivity.map((item) => ({
					sequence: item.sequence,
					...item.teachingActivity,
					pageId: item.pageId,
					metaData: item.metaData,
					imageName: item.imageName,
					imageUrl: item.imageUrl ? getImageUrl(item.imageUrl) : null,
					objectiveId: item.objectiveId
				}));
				lessonPlanItem.objectives = contentExposure.contentExposureObjective.map((item) => ({
					objectiveId: item.contentObjective.objective.id,
					text: item.contentObjective.objective.text,
					contentObjectiveId: item.contentObjectiveId,
					contentExposureId: item.contentExposureId,
					sequence: item.sequence
				}));
				break;
			case LessonPlanItemType4DB.OptionalContent:
				lessonPlanItem.componentContent = lessonPlanItem.optionalContent;
				break;
			case LessonPlanItemType4DB.Activity:
				lessonPlanItem.activityId === Activity.Transition ? lessonPlanItem.transition.duration = toTimeString(lessonPlanItem.transition.duration) : "";
				break;
			default:
				break;
		}
		return lessonPlanItem;
	});
}
function toSeconds(time: string) {
	const times = time.split(":");
	return parseInt(times[0]) * 60 + parseInt(times[1]);
}
@Module({ namespaced: true, name: "MasterPlanState", store: store, dynamic: true })
export default class MasterPlanState extends VuexModule {
	versionGroupService: IVersionGroupService = VersionGroupService();
	unitListManager: IUnitListManager = UnitListManagerService();
	lessonPlanVersionService: ILessonPlanVersionService = LessonPlanVersionService();
	lessonPlanListService: ILessonPlanListService = LessonPlanListService();
	lessonPlanActivitiesService: ILessonPlanActivitiesService = LessonPlanActivitiesService();
	transitionService = TransitionService();
	currentVersionGroup: VersionGroupModel = {} as any;
	currentLessonPlanVersion: ILessonPlanVersionModel = {};
	currentMethodology: MethodologyTypeEnum = MethodologyTypeEnum.Classic;
	currentUnit: VCSLevelModel = {} as VCSLevelModel;
	currentLessons: ILessonPlanListModel[] = [];
	components: IComponentModel[] = [];
	masterPlan: IMasterPlan4DBModel = {} as IMasterPlan4DBModel;
	lessonPlans: ILessonPlanRowModel[] = [];
	originalLessonPlans: ILessonPlanRowModel[] = [];
	transitions: TransitionModel[] = [];
	repConversations: IContentConversationRepModel[] = [];
	currentTransition: TransitionModel = EmptyTransition;
	currentRepConversation: IContentConversationRepModel = EmptyRepConversation;
	currentLessonTransitions: ILessonPlanItemModel[] = [];
	currentLessonNumber4ManageTransition: number = -1;
	loading: boolean = false;
	isREPMethodology: boolean = false;
	lessonPlanEditItems: any[] = [];
	lessonPlan: any = {};
	teachingActivityService = TeachingActivityService();
	signatures: SignatureResoureModel[] = [];
	allUnits: VCSLevelModel[] = [];
	currentSelectedActivity: string = "";
	currentSelectedActivityId: number = 0;
	customActivites: ICustomActivityModel[] = [];

	@Mutation
	async setCurrentSelectedActivity(value: string): Promise<void> {
		this.currentSelectedActivity = value;
	}

	@Mutation
	async setCurrentSelectedActivityId(value: number): Promise<void> {
		this.currentSelectedActivityId = value;
	}

	@Mutation
	async isREP(methodology: MethodologyTypeEnum): Promise<void> {
		this.isREPMethodology = methodology == MethodologyTypeEnum.REP;
	}

	@Mutation
	async setCurrentVersionGroup(versionGroup: VersionGroupModel): Promise<void> {
		this.currentVersionGroup = versionGroup;
	}

	@Mutation
	async setCurrentLessonPlanVersion(lessonPlanVersion: ILessonPlanVersionModel): Promise<void> {
		this.currentLessonPlanVersion = lessonPlanVersion;
	}
	@Mutation
	async setCurrentUnit(unit: VCSLevelModel): Promise<void> {
		this.currentUnit = unit;
	}

	@Mutation
	async setCurrentMethodology(methodology: MethodologyTypeEnum): Promise<void> {
		this.currentMethodology = methodology;
	}

	@Mutation
	async setCurrentLessons(lessons: ILessonPlanListModel[]): Promise<void> {
		this.currentLessons = lessons;
	}

	@Mutation
	async setLessonPlanConversationSpeed(speed: number): Promise<void> {
		this.currentLessons = this.currentLessons.map((cl) => {
			if (cl.sequence === this.currentLessonNumber4ManageTransition) {
				cl.conversationSpeed = speed;
			}
			return cl;
		});
	}

	@Action
	async onAddLesson(): Promise<ILessonPlanListModel> {
		this.setLoading(true);
		const maxSequence: number = Math.max(...this.currentLessons.map((lp) => lp.sequence as number));
		const addingLesson = {
			lessonPlanVersionId: this.currentLessonPlanVersion.id,
			unitId: this.currentUnit.id,
			sequence: maxSequence === -Infinity ? 1 : maxSequence + 1
		};
		const { id } = await this.lessonPlanListService.createLessonPlan(addingLesson);
		const addedLesson = {
			...addingLesson,
			estimatedTime: 0,
			id
		};
		this.currentLessons.push(addedLesson);
		this.lessonPlans[0].estimatedTime =
			this.lessonPlans[0].estimatedTime.length === 0 ? [0] : this.lessonPlans[0].estimatedTime.concat([0]);
		this.setLoading(false);
		return addedLesson;
	}

	@Action
	async onAddLessons(lessonPlanCount): Promise<void> {
		this.setLoading(true);
		const lessonPlansToCreate: ILessonPlanListModel[] = [];
		for (let index = 0; index < lessonPlanCount; index++) {
			lessonPlansToCreate?.push({
				lessonPlanVersionId: this.currentLessonPlanVersion.id,
				unitId: this.currentUnit.id
			});
		}
		const lessonPlanIds = await this.lessonPlanListService.createLessonPlans(lessonPlansToCreate);
		this.setLoading(false);
	}

	@Mutation
	syncEstimatedTimes(lessonPlans: ILessonPlanRowModel[], lessons: ILessonPlanListModel[]): void {
		lessonPlans[0].estimatedTime = [0];
	}

	@Mutation
	setComponents(components: IComponentModel[]): void {
		this.components = components;
	}

	@Mutation
	setCurrentMasterPlan(masterPlan: IMasterPlan4DBModel): void {
		this.masterPlan = masterPlan;
	}

	@Mutation
	setLessonPlans(lessonPlans: ILessonPlanRowModel[]): void {
		this.lessonPlans = lessonPlans;
	}

	@Mutation
	setOriginalLessonPlans(lessonPlansData: ILessonPlanRowModel[]): void {
		this.originalLessonPlans = lessonPlansData;
	}

	@Mutation
	resetLessonPlans(): void {
		this.lessonPlans = cloneDeep(this.originalLessonPlans);
	}

	@Action
	resetLessonPlanToOrigin(): void {
		this.resetLessonPlans();
	}

	@Action
	clearLessonPlans(): void {
		const updatedLessonPlans = this.lessonPlans.map((lp) => {
			const updatedLessonPlan: ILessonPlanRowModel = deepClone(lp);
			if (updatedLessonPlan.rowType === LessonPlanItemType.EstimatedTime) {
				updatedLessonPlan.estimatedTime = updatedLessonPlan.estimatedTime.map((et) => {
					return defaultEstimateTime;
				});
			} else if (updatedLessonPlan.rowType === LessonPlanItemType.Component) {
				updatedLessonPlan.lessonPlanItems = updatedLessonPlan.lessonPlanItems.map((lpi) => {
					lpi.lessonSequence = undefined;
					lpi.taughtType = ComponentTaughtType.None;
					return lpi;
				});

				const exposureCount: number =
					updatedLessonPlan.component &&
					updatedLessonPlan.component.relatedExposureDurations &&
					updatedLessonPlan.component.relatedExposureDurations.length > 0
						? updatedLessonPlan.component.relatedExposureDurations.length
						: minExposureCountInLessonPlan;

				const changedNormalLessonPlanItems: ILessonPlanItemModel[] = updatedLessonPlan.lessonPlanItems.filter(
					(lpis) => lpis.taughtType === ComponentTaughtType.Normal
				);
				updatedLessonPlan.exposure =
					exposureCount > minExposureCountInLessonPlan
						? `${changedNormalLessonPlanItems.length}/${exposureCount}`
						: "";
			} else {
				updatedLessonPlan.lessonPlanItems = [];
			}

			return updatedLessonPlan;
		});

		this.setLessonPlans(updatedLessonPlans);
	}

	@Mutation
	setLessonPlan(lessonPlan): void {
		this.lessonPlan = lessonPlan;
	}

	@Mutation
	setTransitions(transitions: TransitionModel[]): void {
		this.transitions = transitions;
	}

	@Mutation
	setRepConversations(conversations: IContentConversationRepModel[]): void {
		this.repConversations = conversations;
	}

	@Mutation
	setCustomActivites(customActivities: ICustomActivityModel[]): void {
		this.customActivites = customActivities;
	}

	@Mutation
	setTransition(transition: TransitionModel): void {
		this.currentTransition = transition;
	}

	@Mutation
	setRepConversation(conversation: IContentConversationRepModel): void {
		this.currentRepConversation = conversation;
	}

	@Mutation
	setCurrentLesson4ManageTransition(lessonNumber: number): void {
		this.currentLessonNumber4ManageTransition = lessonNumber;
	}

	@Mutation
	setCurrentLessonTransitions(lessonTransitions: ILessonPlanItemModel[]): void {
		// to make lessonSequence unique
		lessonTransitions.forEach((lt, index) => (lt.lessonSequence = index + 1));
		this.currentLessonTransitions = lessonTransitions;
	}

	@Mutation
	setLessonPlanEditItems(lessonPlanEditItems): void {
		this.lessonPlanEditItems = lessonPlanEditItems;
	}

	@Mutation
	onLessonPlansChanged(data: any): void {
		setLessonPlanItem(this.lessonPlans, this.currentLessons, this.transitions, data, this.isREPMethodology, this.customActivites);
	}

	@Action
	async fetchLessonPlans({
		versionGroupId,
		lessonPlanVersionId,
		unitId,
		methodology
	}: {
		versionGroupId: string;
		lessonPlanVersionId: string;
		unitId: number;
		methodology: number;
	}): Promise<void> {
		this.setLoading(true);
		this.isREP(methodology as number);
		const actions: Promise<any>[] = [];
		actions.push(this.versionGroupService.getVersions(false));
		actions.push(this.lessonPlanVersionService.getLessonPlanVersion(lessonPlanVersionId));
		actions.push(TitleListService.getLevels([], true));
		actions.push(
			ResourceService.getLessonPlanContents({
				versionGroupId,
				unitId,
				lessonPlanVersionId
			})
		);
		actions.push(this.lessonPlanVersionService.getMasterPlans(lessonPlanVersionId, unitId));
		actions.push(this.lessonPlanListService.getRepConversations(unitId, versionGroupId))
		actions.push(
				this.transitionService.get({
					versionGroupIds: [versionGroupId],
					vcsLevelId: unitId,
					isCommon: true
				})
			);
		actions.push(this.lessonPlanActivitiesService.getMasterPlanActivities(lessonPlanVersionId, unitId))
		const result: any = await Promise.all(actions);
		const versionGroup: VersionGroupModel = result[0].find((vg) => vg.id === versionGroupId);
		const lessonPlanVersion: ILessonPlanVersionModel = result[1];
		const allUnits: VCSLevelModel[] = result[2];
		const currentUnits: VCSLevelModel[] = filterUnits(allUnits, versionGroup);
		const currentUnit: VCSLevelModel = currentUnits.find((u) => u.id === unitId) as VCSLevelModel;

		const components: IComponentModel[] = result[3];
		const masterPlan: IMasterPlan4DBModel = result[4];
		const lessons: ILessonPlanListModel[] = getLessons(masterPlan);
		const transitions: TransitionModel[] = result[6];
		const repConversations: IContentConversationRepModel[] = result[5];
		const customActivities: ICustomActivityModel[] = result[7].filter(act => act.id !== Activity.Transition && act.id !== Activity.Question && act.id !== Activity.Conversation);
		
		this.setCurrentVersionGroup(versionGroup);
		this.setCurrentLessons(lessons);
		const lessonPlansData = formatLessonPlans(
			components,
			currentUnit,
			masterPlan,
			transitions,
			this.isREPMethodology,
			repConversations,
			customActivities
		);
		this.setLessonPlans(lessonPlansData);
		this.setOriginalLessonPlans(cloneDeep(lessonPlansData));
		this.setCurrentLessonPlanVersion(lessonPlanVersion);
		this.setCurrentMethodology(lessonPlanVersion.methodology as number);
		this.setCurrentUnit(currentUnit);
		this.setComponents(components);
		this.setCurrentMasterPlan(masterPlan);
		this.setTransitions(formatTransitions(transitions));
		this.setRepConversations(formatConversations(repConversations));
		this.setLoading(false);
		this.setCustomActivites(customActivities)
	}

	@Action
	async fetchLessonPlan({
		versionGroupId,
		lessonPlanVersionId,
		unitId,
		lessonPlanId
	}: {
		versionGroupId: string;
		lessonPlanVersionId: string;
		unitId: number;
		lessonPlanId: string;
	}): Promise<void> {
		this.setLoading(true);
		const actions: Promise<any>[] = [];
		actions.push(this.versionGroupService.getVersions(false));
		actions.push(this.lessonPlanVersionService.getLessonPlanVersion(lessonPlanVersionId));
		actions.push(TitleListService.getLevels([], true));
		actions.push(this.lessonPlanListService.getLessonPlanDetails(lessonPlanId, lessonPlanVersionId, unitId));
		const result: any = await Promise.all(actions);
		const versionGroup: VersionGroupModel = result[0].find((vg) => vg.id === versionGroupId);
		const lessonPlanVersion: ILessonPlanVersionModel = result[1];
		const allUnits: VCSLevelModel[] = result[2];
		const currentUnits: VCSLevelModel[] = filterUnits(allUnits, versionGroup);
		const currentUnit: VCSLevelModel = currentUnits.find((u) => u.id === unitId) as VCSLevelModel;

		const lessonPlan = result[3];
		this.setCurrentVersionGroup(versionGroup);
		this.setCurrentLessonPlanVersion(lessonPlanVersion);
		this.setCurrentMethodology(lessonPlanVersion.methodology as number);
		this.setCurrentUnit(currentUnit);
		this.setLessonPlan(lessonPlan);
		this.setLessonPlanEditItems(
			formatLessonPlanEditItems(
				lessonPlan.lessonPlanItem,
				this.signatures.find((sig) => sig.type === ContainerType.Page)!
			)
		);
		this.setLoading(false);
		this.setAllUnits(allUnits);
	}

	@Action
	async swapLessonPlanItems({ lessonPlanId, preLessonPlanItemId, nextLessonPlanItemId }) {
		this.setLoading(true);
		await this.lessonPlanListService.swapLessonPlanItems(lessonPlanId, preLessonPlanItemId, nextLessonPlanItemId);
		const lessonPlan = await this.lessonPlanListService.getLessonPlanDetails(
			lessonPlanId,
			(this.currentLessonPlanVersion as any).id,
			this.currentUnit.id
		);
		this.setLessonPlan(lessonPlan);
		this.setLessonPlanEditItems(
			formatLessonPlanEditItems(
				lessonPlan.lessonPlanItem,
				this.signatures.find((sig) => sig.type === ContainerType.Page)!
			)
		);
		this.setLoading(false);
	}

	@Action
	async fetchLessonPlanItems({ lessonPlanId }) {
		const lessonPlan = await this.lessonPlanListService.getLessonPlanDetails(
			lessonPlanId,
			(this.currentLessonPlanVersion as any).id,
			this.currentUnit.id
		);
		this.setLessonPlan(lessonPlan);
		this.setLessonPlanEditItems(
			formatLessonPlanEditItems(
				lessonPlan.lessonPlanItem,
				this.signatures.find((sig) => sig.type === ContainerType.Page)!
			)
		);
	}

	@Mutation
	setLessonPlanItem(lessonPlanItem: any) {
		const index = this.lessonPlanEditItems.findIndex((d) => d.id === lessonPlanItem.id);
		const data = deepClone(this.lessonPlanEditItems[index]);
		Object.getOwnPropertyNames(lessonPlanItem).forEach((key) => {
			data[key] = lessonPlanItem[key];
		});
		this.lessonPlanEditItems.splice(index, 1, data);
	}

	@Action
	async addQuestion() {
		this.currentLessonTransitions.push({
			itemType: LessonPlanItemType.Activity,
			componentId: "",
			componentName: fmtMsg(LessonPlanLocale.LessonPlanPlayListBuilderCellQuestionTitle),
			lessonNumber: this.currentLessonNumber4ManageTransition,
			taughtType: ComponentTaughtType.Normal,
			lessonSequence: this.currentLessonTransitions.length + 1,
			activityId: Activity.Question
		});
	}

	@Action
	async addConversation({ compId, name }) {
		this.currentLessonTransitions.push({
			itemType: LessonPlanItemType.Activity,
			componentId: compId,
			componentName: name,
			lessonNumber: this.currentLessonNumber4ManageTransition,
			taughtType: ComponentTaughtType.Normal,
			lessonSequence: this.currentLessonTransitions.length + 1,
			activityId: Activity.Conversation
		});
	}

	@Action
	async addSelectedActivity() {
		this.currentLessonTransitions.push({
			itemType: LessonPlanItemType.Activity,
			componentId: "",
			componentName: this.currentSelectedActivity,
			lessonNumber: this.currentLessonNumber4ManageTransition,
			taughtType: ComponentTaughtType.Normal,
			lessonSequence: this.currentLessonTransitions.length + 1,
			activityId: this.currentSelectedActivityId
		});
	}

	@Action
	async fetchCurrentLessonTransitions(lessonNumber: number) {
		this.setCurrentLessonTransitions(
			getLessonTransitions(this.lessonPlans, lessonNumber, ComponentTaughtType.Normal, true)
		);
	}

	@Action
	async addTransition({
		transition,
		isCustom,
		selectedItemType
	}: {
		transition: TransitionModel;
		isCustom: boolean;
		selectedItemType: PlaylistItemType;
	}) {
		const mergedTransition = {
			...transition,
			text: transition.text.trim(),
			isCommon: false,
			versionGroupId: this.currentVersionGroup.id,
			vcsLevelId: this.currentUnit.id
		};
		const { id } = isCustom ? await this.transitionService.createTransition(mergedTransition) : transition.id;
		const currentTransition = isCustom ? { ...mergedTransition, id, lessonPlans: [] } : transition;
		if (isCustom) {
			this.transitions.push(currentTransition);
		}
		this.setTransition(currentTransition);
		this.currentLessonTransitions.push({
			activityId: selectedItemType === PlaylistItemType.Question ? Activity.Question : selectedItemType === PlaylistItemType.Conversation ? Activity.Conversation : Activity.Transition,
			itemType: LessonPlanItemType.Activity,
			componentId: currentTransition.id,
			componentName: currentTransition.text,
			lessonNumber: this.currentLessonNumber4ManageTransition,
			taughtType: ComponentTaughtType.Normal,
			lessonSequence: this.currentLessonTransitions.length + 1
		});
	}

	@Action
	removeLessonTransition(lessonTransition: ILessonPlanItemModel) {
		const lessonTransitionIndex: number = this.currentLessonTransitions.findIndex(
			(lpi) =>
				lpi.lessonNumber === this.currentLessonNumber4ManageTransition &&
				lpi.lessonSequence === lessonTransition.lessonSequence
		);
		if (lessonTransitionIndex >= 0) {
			this.currentLessonTransitions.splice(lessonTransitionIndex, 1);
			this.currentLessonTransitions
				.filter((clt, index) => index >= lessonTransitionIndex)
				.forEach((lt, index) => (lt.lessonSequence = (lt.lessonSequence as number) - 1));
		}
	}

	@Action
	saveLessonTransition(conversationSpeed?: number) {
		// update sequences of transition row
		const transitionRow: ILessonPlanRowModel = getTransitionRow(this.lessonPlans, this.isREPMethodology);
		remove(transitionRow.lessonPlanItems, (lpi) => lpi.lessonNumber === this.currentLessonNumber4ManageTransition);
		transitionRow.lessonPlanItems.push(
			...this.currentLessonTransitions
				.filter(
					(clt) => clt.itemType === LessonPlanItemType.Activity
				)
				.map((clt) => {
					const itemIndex: number = this.currentLessonTransitions.findIndex(
						(clt) => clt.componentId === clt.componentId
					);
					return { ...clt, lessonSequence: itemIndex >= 0 ? itemIndex : clt.lessonSequence };
				})
		);
		// update sequences of component row
		const currentLessons: ILessonPlanItemModel[] = getLessonTransitions(
			this.lessonPlans,
			this.currentLessonNumber4ManageTransition,
			ComponentTaughtType.Normal,
			false
		);
		// 1.component
		currentLessons
			.filter((cl) => cl.itemType === LessonPlanItemType.Component)
			.forEach((cl) => {
				const itemIndex: number = this.currentLessonTransitions.findIndex(
					(clt) => clt.componentId === cl.componentId
				);
				if (itemIndex >= 0) {
					cl.lessonSequence = itemIndex + 1;
				}
			});
		// 2.transition
		const transitionGroups = groupBy(
			currentLessons.filter(
				(cl) => cl.itemType === LessonPlanItemType.Activity
			),
			(cl) => cl.componentId
		);
		Object.keys(transitionGroups).forEach((tg) => {
			const tgLessons: ILessonPlanItemModel[] = transitionGroups[tg];
			const currentLessonTransitions: ILessonPlanItemModel[] = this.currentLessonTransitions.filter(
				(clt) => clt.componentId === tgLessons[0].componentId && clt.itemType === tgLessons[0].itemType
			);
			if (tgLessons.length === currentLessonTransitions.length) {
				tgLessons.forEach((tg, index) => {
					tg.lessonSequence = currentLessonTransitions[index].lessonSequence;
				});
			}
		});
		// 3.optional component
		const optionalLessons: ILessonPlanItemModel[] = getLessonTransitions(
			this.lessonPlans,
			this.currentLessonNumber4ManageTransition,
			ComponentTaughtType.Optional,
			false
		);
		optionalLessons
			.filter((cl) => cl.taughtType === ComponentTaughtType.Optional)
			.forEach((cl, index) => (cl.lessonSequence = this.currentLessonTransitions.length + index + 1));
		// update estimated time
		setEstimatedTime(this.lessonPlans, this.transitions, this.currentLessons, this.customActivites);
		this.setLessonPlanConversationSpeed(conversationSpeed as number);
	}

	@Action
	setDuration({ record: { id }, values }) {
		this.setLessonPlanItem({ id, duration: values });
	}

	@Action
	setRelatedItems({ record: { id }, values }) {
		this.setLessonPlanItem({ id, relatedItems: values });
	}

	@Action
	setTeachingActivity({ record: { id }, values }) {
		this.setLessonPlanItem({ id, teachingActivities: values });
	}

	@Action
	setExposureTeachingActivity({ record: { id }, values }) {
		this.setLessonPlanItem({ id, contentExposureTeachingActivity: values });
	}

	@Action
	setObjectives({ record: { id }, values }) {
		this.setLessonPlanItem({ id, objectives: values });
	}

	@Action
	editingLessonPlanItem({ id, isEdit }) {
		this.setLessonPlanItem({ id, isEdit });
	}

	@Action
	async updateLessonPlanItem(record) {
		await this.editLessonPlanItem(record);
		await this.fetchLessonPlanItems(record);
	}

	@Action
	async editLessonPlanItem(record) {
		record = await this.createCustomActivites(record);
		return ContentExposureService.update(
			((d) => {
				const contentExposure = d.contentExposure;
				contentExposure.ignoreMethodology = true;
				contentExposure.minPlanningTime = toSeconds(d.duration.start);
				contentExposure.maxPlanningTime = toSeconds(d.duration.end);
				contentExposure.contentExposureRelatedItem = d.relatedItems.map((item) => ({
					...item
				}));
				contentExposure.contentExposureTeachingActivity = d.teachingActivities.map((item) => ({
					contentExposureId: d.id,
					teachingActivityId: item.id,
					sequence: item.sequence,
					teachingActivity: item,
					pageId: item.pageId,
					imageUrl: item.imageUrl,
					metaData: item.metaData,
					imageName: item.imageName,
					objectiveId: item.objectiveId
				}));
				contentExposure.contentExposureObjective = d.objectives.map((item) => ({
					contentExposureId: item.contentExposureId || d.id,
					contentObjective: item.contentObjective,
					sequence: item.sequence,
					contentObjectiveId: item.contentObjectiveId || item.contentObjective.id
				}));
				delete contentExposure.content;
				contentExposure.contentExposureObjective?.forEach((v) => {
					delete v.contentObjective;
				});
				contentExposure.contentExposureTeachingActivity?.forEach((v) => {
					delete v.teachingActivity;
				});
				return contentExposure;
			})(deepClone(record))
		);
	}

	@Action
	async createCustomActivites(record) {
		const allActivities = deepClone(record.teachingActivities);
		const customActivitiesToBeAdded = allActivities.filter((f) => f.isNew);
		// add the custom activities to db
		if (customActivitiesToBeAdded.length) {
			try {
				const { item1, item2 } = await this.teachingActivityService.createMultipleTeachingActivity(
					customActivitiesToBeAdded
				);
				// update the selected activities
				const updatedActivities = allActivities.map((f) => {
					const updatedItem = item1.find((i) => i.text === f.text);
					if (updatedItem) {
						f.id = updatedItem?.id;
						f.contentExposures = [];
						delete f.isNew;
					}
					return f;
				});
				record.teachingActivities = updatedActivities;
			} catch (e) {
				// show error
			}
		}
		return record;
	}

	@Action
	async onSaveLessonPlans(data: any): Promise<void> {
		this.setLoading(true);
		const result: boolean = await this.lessonPlanVersionService.saveMasterPlans(
			this.currentLessonPlanVersion.id as string,
			this.currentUnit.id,
			{
				lessonPlan: formatLessonPlans4Save(this.currentLessons, this.lessonPlans, this.isREPMethodology)
			} as IMasterPlan4DBModel
		);
		this.setLoading(false);
	}

	@Mutation
	setLoading(loading: boolean): void {
		this.loading = loading;
	}

	@Action
	async getSignature() {
		const signature = await ContentExposureService.getTeachingActivitySas();
		this.setSignature(signature);
	}

	@Mutation
	setSignature(signature: SignatureResoureModel) {
		this.signatures = [...this.signatures, { ...signature }];
	}

	@Mutation
	async setAllUnits(units: VCSLevelModel[]): Promise<void> {
		this.allUnits = units;
	}

	get getCurrentVersionGroup() {
		return this.currentVersionGroup;
	}

	get getCurrentMethodology() {
		return this.currentMethodology;
	}

	get getCurrentUnit() {
		return this.currentUnit;
	}
}
